import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocationParams, useSyncGlobalApp } from 'hooks';
import { useRowsCreatives } from 'features/creatives';
import type { SelectChangeEvent } from '@mui/material';
import { Box, Tooltip, Typography } from '@mui/material';
import { Checkbox, ChipInput, CircularProgress, Select, SuspenseErrorFallback } from 'components/common';
import { DataGrid, type GridRenderCellParams } from '@mui/x-data-grid';
import type { GridRowSelectionModel } from '@mui/x-data-grid/models/gridRowSelectionModel';
import { FormContainer } from 'components/layout';
import type { CreativeEntry } from 'types/shared';
import { useAdTypes } from '../api/getAdTypes';
import { isEmpty } from 'lodash-es';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { Network } from '../../../constants';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';
import { isFacebookAdTypeCode } from '../constants';

const CREATIVES_LIMIT = 30;

const ScalingEgCreativesForm: FC = () => {
  const { watch, setValue, trigger } = useFormContext();
  const { adType: adTypeParam, setAdType: setAdTypeParam } = useLocationParams();

  const handleAppChange = useCallback(() => {
    setValue('selectedCampaigns', [], { shouldValidate: true });
    setValue('selectedAdSets', [], { shouldValidate: true });
    setValue('selectedCreatives', [], { shouldValidate: true });
  }, [setValue]);

  useSyncGlobalApp(handleAppChange);

  useEffect(() => {
    setValue('packsWithCreatives', [], { shouldValidate: true });
    setValue('activePacksWithCreatives', null, { shouldValidate: true });
  }, [setValue]);

  const platform = watch('platform') as string;
  const adType = watch('adType') as string;
  const selectedCreatives = watch('selectedCreatives') as string[];
  const selectedOnly = watch('selectedCreativesOnly') as boolean;
  const filterCreativesByName = watch('filterCreativesByName') as string[];

  const { rows, isLoading, isError, error } = useRowsCreatives({
    params: {
      network: Network.FACEBOOK,
    },
  });

  const { data: adTypes, isLoading: isAdTypesLoading } = useAdTypes({
    params: { platform },
    config: {
      enabled: !!platform,
      select: (data) =>
        data.map((item) => ({
          value: item.code,
          label: item.name,
        })),
    },
  });

  const isDLO = adType?.toLowerCase() === 'dlo';

  const columns = useMemo(() => {
    const currentAdTypeValue =
      adTypes?.find((item) => item.value === adType)?.label ?? adType;

    return [
      { field: 'creative_id', headerName: 'Creative ID', width: 120 },
      {
        field: 'name',
        headerName: 'Name',
        width: 450,
        renderCell: (params: GridRenderCellParams<CreativeEntry>) => {
          const isSelectable =
            (isDLO && !isEmpty(params.row.locales)) || !isDLO;
          const tooltipMessage = !isSelectable
            ? `Can't be used for ${currentAdTypeValue} without localizations`
            : null;

          return (
            <Tooltip title={tooltipMessage} arrow placement="top">
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.25,
                  height: '100%',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2">{params.row.name}</Typography>
                <Typography
                  variant="caption"
                  sx={(theme) => ({ color: theme.palette.text.disabled })}
                >
                  {isSelectable && !isEmpty(params.row.locales)
                    ? `Loc: ${params.row.locales?.join(', ')}`
                    : 'No localizations'}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      { field: 'app', headerName: 'App', width: 120 },
      { field: 'creative_type', headerName: 'Type', width: 120 },
      { field: 'created', headerName: 'Created', width: 120 },
    ];
  }, [adType, adTypes, isDLO]);

  const filteredRows = useMemo(() => {
    const nameFilteredRows = rows?.filter((row) => {
      if (!filterCreativesByName?.length) {
        return true;
      }

      return filterCreativesByName.every((filterValue) =>
        row.name.toLowerCase().includes(filterValue.toLowerCase()),
      );
    });

    if (!selectedOnly) {
      return nameFilteredRows;
    }

    return nameFilteredRows?.filter((row) => selectedCreatives.includes(row.id));
  }, [rows, selectedCreatives, selectedOnly, filterCreativesByName]);

  const rowSelectionModel = useMemo(() => {
    const availableIds = new Set((filteredRows ?? []).map((row) => row.id));
    return selectedCreatives.filter((id) => availableIds.has(id));
  }, [filteredRows, selectedCreatives]);

  const onSelectionModelChange = useCallback(
    (selectedRows: GridRowSelectionModel) => {
      const parsedSelectedRows = selectedRows.slice(0, CREATIVES_LIMIT).map(String);

      setValue('selectedCreatives', parsedSelectedRows, { shouldValidate: true });
      datadogActions.trackDatadogAction(DATADOG_EVENT.creativeSelected, {
        selectedCount: parsedSelectedRows.length,
        totalRowsCount: filteredRows?.length ?? 0,
        creativeTab: 'scaling_eg',
        adType: adType || undefined,
      });
    },
    [adType, filteredRows, setValue],
  );

  const handleAdTypeChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const nextValue = String(event.target.value ?? '');
      setAdTypeParam(isFacebookAdTypeCode(nextValue) ? nextValue : undefined);

      if (selectedCreatives.length > 0) {
        setValue('selectedCreatives', [], { shouldValidate: true });
      }
    },
    [selectedCreatives.length, setAdTypeParam, setValue],
  );

  // Browser back/forward changes the URL param; mirror it into the form.
  useEffect(() => {
    if (!adTypeParam || adTypeParam === adType) return;
    setValue('adType', adTypeParam, { shouldValidate: true });
  }, [adType, adTypeParam, setValue]);

  useEffect(() => {
    void trigger('selectedCreatives');
    void trigger('adType');
  }, [trigger]);

  if (isError) {
    return (
      <SuspenseErrorFallback
        title="Choose Creatives"
        message={
          (
            error as {
              cause?: { error?: string };
            }
          )?.cause?.error ?? 'An error occurred while fetching creatives'
        }
      />
    );
  }

  return (
    <FormContainer
      title={adType ? 'Choose Creatives' : 'Select Ad type'}
      sx={{ flexDirection: 'column', gap: 2 }}
    >
      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 2, width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 2,
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flex: 2,
              flexDirection: 'row',
              alignItems: 'start',
              flexWrap: 'nowrap',
            }}
          >
            <Box sx={{ maxWidth: '140px' }}>
              <Select
                name="adType"
                inputLabel="Ad type"
                disabled={isEmpty(adTypes) || isAdTypesLoading}
                options={adTypes ?? []}
                handleChange={handleAdTypeChange}
                autoFocus
                confirmation={
                  selectedCreatives.length > 0
                    ? {
                      title: 'Changing Ad type will reset selected creatives',
                      bodyText: 'Changing the Ad type will reset selected creatives',
                      cancelButtonTitle: 'Cancel',
                      submitButtonTitle: 'Change Ad Type',
                      onSubmit: () => {
                        setValue('selectedCreatives', [], { shouldValidate: true });
                      },
                    }
                    : undefined
                }
              />
            </Box>
            <Box sx={{ flex: 2 }}>
              <ChipInput
                name="filterCreativesByName"
                inputLabel="Enter creative name"
              />
            </Box>
            <Checkbox
              dataTestId="selectedCreativesOnly"
              name="selectedCreativesOnly"
              label="Selected only"
              controlLabelSx={{ whiteSpace: 'nowrap' }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            height: '100%',
            width: '100%',
            minHeight: 400,
            position: 'relative',
          }}
        >
          {isLoading && isEmpty(filteredRows) ? (
            <CircularProgress title="Creatives are loading..." />
          ) : isEmpty(filteredRows) ? (
            <Typography
              data-testid="noCreativesPlaceholder"
              sx={(theme) => ({
                color: theme.palette.grey[400],
                fontSize: theme.typography.h4.fontSize,
              })}
            >
              {!adType ? 'Select Ad type to add creatives' : 'No creatives found'}
            </Typography>
          ) : (
            <Box
              sx={{
                position: 'absolute',
                overflowY: 'auto',
                width: '100%',
                height: '100%',
              }}
            >
              <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
                {isLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      display: 'flex',
                      alignContent: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      zIndex: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    }}
                  >
                    <CircularProgress title="Creatives are loading..." />
                  </Box>
                )}
                <DataGrid
                  rows={filteredRows ?? []}
                  columns={columns}
                  localeText={{
                    noRowsLabel: !adType ? 'Select Ad type to add creatives' : '',
                  }}
                  checkboxSelection
                  rowSelectionModel={rowSelectionModel}
                  onRowSelectionModelChange={onSelectionModelChange}
                  keepNonExistentRowsSelected
                  disableVirtualization
                  hideFooterPagination
                  getRowId={(row) => row.id}
                  isRowSelectable={(params) => {
                    const isDLOLocalesEmpty = isDLO && !isEmpty(params.row.locales);
                    const isUpToLimit =
                      selectedCreatives.length < CREATIVES_LIMIT ||
                      selectedCreatives.includes(params.row.id);
                    return isDLOLocalesEmpty || !isDLO || isUpToLimit;
                  }}
                  slots={{
                    footer: () =>
                      rows?.length ? (
                        <Box
                          sx={(theme) => ({
                            p: 2,
                            height: 54,
                            minHeight: 54,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            cursor: 'default',
                          })}
                        >
                          <Typography variant="body2">
                            {rows.length} found /
                          </Typography>
                          {selectedCreatives.length === CREATIVES_LIMIT ? (
                            <>
                              <InfoIcon color="primary" sx={{ fontSize: 21 }} />
                              <Typography variant="body2">
                                {`Up to ${CREATIVES_LIMIT} creatives can be selected`}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2">
                              {selectedCreatives.length} selected
                            </Typography>
                          )}
                        </Box>
                      ) : null,
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </FormContainer>
  );
};

export default ScalingEgCreativesForm;
